import { useState, useMemo, useEffect } from 'react';
import { defaultPlatform } from '../../../platform';

const normalizeFields = (fields) => {
    if (!fields) return null;
    if (Array.isArray(fields)) return new Set(fields.map(String));
    return new Set(String(fields).split(',').map((s) => s.trim()).filter(Boolean));
};

const pickNumber = (...candidates) => {
    for (let i = 0; i < candidates.length; i += 1) {
        const v = candidates[i];
        if (v === null || v === undefined || v === '') continue;
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isFinite(n)) return n;
    }
    return null;
};

const formatMoney = (value) => {
    if (value === null || value === undefined) return null;
    const n = pickNumber(value);
    if (n === null) return null;
    return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const isProductOutOfStock = (product) => {
    if (!product) return false;
    if (product.is_out_of_stock === true) return true;
    const commerce = product.commerce_settings;
    const enforceStock = !commerce || commerce.stop_selling_out_of_stock === true;
    if (!enforceStock) return false;
    const amount = product.sellable_amount ?? product.sellable_amount_field;
    if (amount === null || amount === undefined) return false;
    return Number(amount) <= 0;
};

const getOutOfStockLabel = (product, i18n) => {
    const custom = String(product?.out_of_stock_label || '').trim();
    return custom || i18n.t('Out of stock');
};

const ProductCard = ({ product, fields = null, platform = defaultPlatform }) => {
    const i18n = platform.i18n;
    const {
        buildVariantAttributeContext,
        getAllAttributeValueOptions,
        getSelectedVariant,
        selectVariantAttribute,
        getDefaultVariantSelection,
        getStoreVariants,
    } = platform.variants;
    const { CartDrawer, Icon, ProductVariantSelector, ReviewStars } = platform.ui;
    const addProductToCart = (args) => platform.cart.add(args);

    const [selectedVariant, setSelectedVariant] = useState({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [message, setMessage] = useState(null);
    const [showCartDrawer, setShowCartDrawer] = useState(false);
    const [cartSnapshot, setCartSnapshot] = useState(null);

    const { attrs, attrMap } = useMemo(() => buildVariantAttributeContext(product), [product]);
    const attrOptionsExist = useMemo(() => {
        if (!product || !attrs.length) return false;
        return attrs.some((a) => getAllAttributeValueOptions(product, a.short_code, attrMap).length > 0);
    }, [product, attrs, attrMap]);

    const variantProducts = getStoreVariants(product);
    const productObjectCount = variantProducts.length;
    const needsSimplePoPicker = productObjectCount > 1 && attrs.length === 0;
    const hasAttrSelectors = attrs.length > 0 && attrOptionsExist && productObjectCount > 0;
    const cartRequiresVariant = productObjectCount > 1;

    const [selectedPoId, setSelectedPoId] = useState(null);

    // Default each attribute to its first valid option whenever the product changes.
    useEffect(() => {
        if (!product?.id || !hasAttrSelectors) {
            setSelectedVariant({});
            return;
        }
        setSelectedVariant(getDefaultVariantSelection(product, attrMap, attrs));
    }, [product?.id, hasAttrSelectors, product, attrMap, attrs]);

    useEffect(() => {
        if (!product?.id || !needsSimplePoPicker) {
            setSelectedPoId(null);
            return;
        }
        const list = variantProducts;
        setSelectedPoId((prev) => {
            if (prev && list.some((p) => p.id === prev)) return prev;
            return list[0]?.id ?? null;
        });
    }, [product?.id, needsSimplePoPicker, variantProducts]);

    const variantResult = useMemo(() => {
        if (needsSimplePoPicker && selectedPoId) {
            const vpo = variantProducts.find((p) => p.id === selectedPoId);
            if (vpo) {
                return {
                    product_object: vpo,
                    product_number: vpo.product_number,
                    label: vpo.product_number,
                };
            }
        }
        return getSelectedVariant(product, selectedVariant, attrMap);
    }, [needsSimplePoPicker, selectedPoId, product, selectedVariant, attrMap]);

    // Get the selected product object (variant) or default to first product_object
    const selectedProductObject = useMemo(() => {
        if (variantResult?.product_object) {
            return variantResult.product_object;
        }
        // If no variant selected but product has product_objects, use first one
        if (variantProducts.length > 0) {
            return variantProducts[0];
        }
        // Fallback to product_object if available
        return product?.product_object || null;
    }, [variantResult, product, variantProducts]);

    const po = selectedProductObject || product?.product_object || product?.product_options?.[0]?.product_object || {};
    const categoryName = po.category?.name;
    const reviews = po.reviews || [];
    const displayRatingRaw = Number(product?.average_rating ?? po?.average_rating);
    const displayRating = Number.isFinite(displayRatingRaw)
        ? Math.min(5, Math.max(0, displayRatingRaw))
        : null;
    const baseUrl = product?.url || po.url || '#';
    const name = po.name || product?.name || '';
    const sequence = product?.product_object?.sequence || '';
    const productImages = useMemo(() => {
        const sortImgs = (list) => [...(list || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        const fromPo = sortImgs(po?.productimages_objects);
        if (fromPo.length > 0) return fromPo;
        const fromProduct = sortImgs(product?.productimages_objects);
        if (fromProduct.length > 0) return fromProduct;
        const objs = variantProducts;
        for (let i = 0; i < objs.length; i += 1) {
            const imgs = sortImgs(objs[i]?.productimages_objects);
            if (imgs.length > 0) return imgs;
        }
        return [];
    }, [po, product, variantProducts]);
    const getProductImageUrl = (imageObj) => imageObj?.image_webp || imageObj?.image || null;
    const firstImageUrl = productImages.length >= 1 ? getProductImageUrl(productImages[0]) : null;
    const secondImageUrl = productImages.length >= 2 ? getProductImageUrl(productImages[1]) : null;
    const imageMedium =
        firstImageUrl
        || po.image_webp
        || po.image_medium
        || po.image
        || product?.product_object?.image_medium
        || product?.product_object?.image_webp
        || product?.product_object?.image
        || product?.image_medium
        || product?.image_webp
        || product?.image
        || null;
    const imageHover =
        (secondImageUrl && secondImageUrl !== imageMedium ? secondImageUrl : null)
        || po.image_hover_webp
        || po.image_hover_medium
        || po.image_hover
        || product?.product_object?.image_hover_medium
        || product?.product_object?.image_hover_webp
        || product?.product_object?.image_hover
        || product?.image_hover_medium
        || product?.image_hover_webp
        || product?.image_hover
        || null;
    const tempUnavailable = po.tempUnavailable;
    const url = variantResult
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variantResult.product_number)}`
        : baseUrl;

    // Get main product ID - use selected variant's product ID
    // product_objects[].id is the Product ID (from children_products)
    const mainProductId = useMemo(() => {
        if (selectedProductObject?.id) {
            return selectedProductObject.id;
        }
        // If product doesn't have variants, use the product's own ID
        return product?.id || null;
    }, [selectedProductObject, product]);

    const stockProduct = selectedProductObject || product;
    const outOfStock = isProductOutOfStock(stockProduct);
    const outOfStockLabel = getOutOfStockLabel(stockProduct, i18n);

    // Add to cart function
    const addToCart = async () => {
        if (outOfStock) {
            setMessage(outOfStockLabel);
            setTimeout(() => setMessage(null), 3000);
            return;
        }
        setIsAddingToCart(true);
        setMessage(null);

        try {
            const response = await addProductToCart({
                product,
                variantResult,
                selectedOptions: {},
                quantity: 1,
                hasVariants: cartRequiresVariant,
                mainProductId,
            });

            setCartSnapshot(response?.serializer || null);
            setShowCartDrawer(true);
        } catch (error) {
            console.error('Error adding to cart:', error);
            setMessage(error.message || i18n.t('Error adding product to cart'));
            setTimeout(() => setMessage(null), 3000);
        } finally {
            setIsAddingToCart(false);
        }
    };

    const discountLabel = product?.discount_group?.discount_label;
    const discountHtml = discountLabel ? (
        <span className="discount-tag">{discountLabel}</span>
    ) : null;

    const fromTrans = i18n.t('from_trans');
    const tempUnavTrans = i18n.t('temp_unav_trans');

    if (!product) return null;

    const wanted = normalizeFields(fields);
    const show = (k) => !wanted || wanted.size === 0 || wanted.has(String(k));

    // Prefer fully selected variant; otherwise min among children (parents are often 0).
    const pricedVariant = variantResult?.product_object || null;
    const childPrices = variantProducts
        .map((child) => ({
            price: pickNumber(child?.price),
            after: pickNumber(child?.price_after_discount, child?.price),
        }))
        .filter((row) => row.after != null || row.price != null);
    const minChildAfter = childPrices.reduce((min, row) => {
        const n = row.after ?? row.price;
        if (n == null) return min;
        return min == null || n < min ? n : min;
    }, null);
    const minChildPrice = childPrices.reduce((min, row) => {
        const n = row.price ?? row.after;
        if (n == null) return min;
        return min == null || n < min ? n : min;
    }, null);

    let rawPrice;
    let rawPriceAfterDiscount;
    if (pricedVariant) {
        rawPrice = pickNumber(pricedVariant.price);
        rawPriceAfterDiscount = pickNumber(pricedVariant.price_after_discount, pricedVariant.price);
    } else if (minChildAfter != null || minChildPrice != null) {
        rawPrice = minChildPrice;
        rawPriceAfterDiscount = minChildAfter ?? minChildPrice;
    } else {
        rawPrice = pickNumber(product?.price);
        rawPriceAfterDiscount = pickNumber(product?.price_after_discount, product?.price);
    }

    const formattedPrice = formatMoney(rawPrice);
    const formattedAfterDiscount = formatMoney(rawPriceAfterDiscount);

    const hasBothPrices = rawPrice != null && rawPriceAfterDiscount != null;
    const showDiscount = hasBothPrices && rawPriceAfterDiscount < rawPrice;
    const showFromLabel = Boolean(productObjectCount > 0 && !pricedVariant);
    // Standalone products can always add; parents/variants need a resolved selection first.
    const showAddToCart = productObjectCount === 0 || Boolean(variantResult);

    const handleAttributeChange = (shortCode, valueId) => {
        setSelectedVariant((prev) => selectVariantAttribute(product, attrMap, attrs, prev, shortCode, valueId));
    };

    return (
        <div className="product-card">
            <CartDrawer open={showCartDrawer} cart={cartSnapshot} onClose={() => setShowCartDrawer(false)} />
            <div className="product-card-inner product-card-inner--store my-2 d-flex flex-column h-100">
                <a className="product-image text-decoration-none text-dark" href={url}>
                    <div className="product-tags">
                        {discountHtml}
                        {tempUnavailable ? (
                            <div className="product-unavailable bg-danger">{tempUnavTrans}</div>
                        ) : null}
                        {outOfStock ? (
                            <div className="product-unavailable bg-danger">{outOfStockLabel}</div>
                        ) : null}
                    </div>
                    <div className="image-wrapper w-100 position-relative">
                        {imageMedium ? (
                            <div>
                                <img
                                    alt={name}
                                    className="main-image store-product-card__img lazyload"
                                    src={imageMedium}
                                />
                                <img
                                    alt=""
                                    className="hover-image store-product-card__img lazyload"
                                    src={imageHover || imageMedium}
                                />
                            </div>
                        ) : (
                            <div
                                className="d-flex align-items-center justify-content-center bg-light text-muted user-select-none"
                                style={{ position: 'absolute', inset: 0 }}
                                role="img"
                                aria-label={i18n.t('No image') || 'No image'}
                            >
                                <i
                                    className="bi bi-image"
                                    style={{ fontSize: '2.75rem', opacity: 0.35 }}
                                    aria-hidden="true"
                                />
                            </div>
                        )}
                    </div>
                </a>
                <div className="product-description flex-grow-1">
                    {show('name') ? (
                        <a href={url} className="text-decoration-none text-dark">
                            <h3 className="hover product-title mb-1">{name}{sequence ? ` ${sequence}` : ''}</h3>
                        </a>
                    ) : null}
                    {show('category') && categoryName ? (
                        <small className="u-small-text u-text u-text-variant u-text-2 d-block mb-1">{categoryName}</small>
                    ) : null}
                    {needsSimplePoPicker || (show('variants') && hasAttrSelectors) ? (
                        <ProductVariantSelector
                            size="compact"
                            product={product}
                            attrs={hasAttrSelectors ? attrs : []}
                            attrMap={attrMap}
                            selectedVariant={selectedVariant}
                            onSelect={handleAttributeChange}
                            selectLabel={needsSimplePoPicker ? (i18n.t('Variant') || i18n.t('Option')) : null}
                            selectValue={selectedPoId || ''}
                            selectOptions={
                                needsSimplePoPicker
                                    ? variantProducts.map((p) => ({
                                        id: p.id,
                                        label: [p.product_number, p.name].filter(Boolean).join(' · ') || String(p.id),
                                    }))
                                    : null
                            }
                            onSelectChange={(id) => setSelectedPoId(id || null)}
                        />
                    ) : null}
                    {show('reviews') && displayRating != null && displayRating > 0 ? (
                        <div className="d-flex align-items-center gap-2 mt-1 mb-1">
                            <ReviewStars stars={displayRating} size="0.92rem" gap="0.12rem" />
                            <span className="text-muted small tabular-nums">{displayRating.toFixed(1)}</span>
                            {reviews.length > 0 ? (
                                <span className="text-muted small">· {reviews.length} {i18n.t('reviews')}</span>
                            ) : null}
                        </div>
                    ) : null}
                    {(show('price') || show('price_discount')) && formattedAfterDiscount != null ? (
                        <div className="product-price mt-auto pt-2">
                            {showFromLabel ? (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>{fromTrans}</span>
                            ) : null}
                            <div className="d-flex align-items-baseline flex-wrap gap-2">
                                <h4 className="price-tag mb-0">€{formattedAfterDiscount}</h4>
                                {showDiscount && formattedPrice != null ? (
                                    <span className="old-price">€{formattedPrice}</span>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    <div className="add-to-cart-section mt-3 pt-2 border-top border-light">
                        {message && (
                            <div className={`alert alert-${message.includes('Error') ? 'danger' : 'success'} py-1 px-2 mb-2`} style={{ fontSize: '0.875rem' }}>
                                {message}
                            </div>
                        )}
                        <div className="d-flex flex-row gap-2 align-items-stretch">
                            <a
                                href={url}
                                className="btn btn-sm btn-outline-dark d-inline-flex align-items-center justify-content-between rounded-3 flex-grow-1 store-card-view-btn"
                            >
                                <span className="lh-1">{i18n.t('View product')}</span>
                                <Icon
                                    name="ICON_ARROW_RIGHT"
                                    width="16px"
                                    height="16px"
                                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                    style={{ lineHeight: 0 }}
                                />
                            </a>
                            {showAddToCart ? (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary d-inline-flex align-items-center justify-content-center rounded-3 shadow-sm store-card-action-btn store-card-add-cart"
                                    onClick={addToCart}
                                    disabled={isAddingToCart || outOfStock}
                                    aria-label={outOfStock ? outOfStockLabel : (isAddingToCart ? i18n.t('Adding...') : i18n.t('Add to Cart'))}
                                    title={outOfStock ? outOfStockLabel : (isAddingToCart ? i18n.t('Adding...') : i18n.t('Add to Cart'))}
                                    aria-busy={isAddingToCart}
                                >
                                    {isAddingToCart ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    ) : (
                                        <Icon
                                            name="ICON_PLUS"
                                            width="16px"
                                            height="16px"
                                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ lineHeight: 0 }}
                                        />
                                    )}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
